import { useState } from 'react';
import { motion } from 'framer-motion';
import { AlertTriangle } from 'lucide-react';

/**
 * Edit case count modal — adviser only.
 *
 * Pre-fills the inputs with the file's current case_count and case_labels
 * and lets the adviser bump the count up or down with the same dynamic
 * add/remove behaviour as the submission form. If reducing the count
 * would orphan one or more uploaded screenshots, a second confirmation
 * step lists each affected case by label and asks for a deliberate
 * confirm before the destructive change is applied.
 */
export default function EditCasesModal({ file, screenshotsByIndex, actionLoading, onClose, onApply }) {
  const [count, setCount] = useState(file.caseCount || 1);
  const [labels, setLabels] = useState(() =>
    Array.from({ length: file.caseCount || 1 }, (_, i) => (file.caseLabels || [])[i] || '')
  );
  const [localError, setLocalError] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);

  const handleCountChange = (raw) => {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) return;
    const clamped = Math.max(1, Math.min(10, parsed));
    setCount(clamped);
    setLabels((prev) => {
      if (clamped === prev.length) return prev;
      if (clamped > prev.length) {
        return [...prev, ...Array(clamped - prev.length).fill('')];
      }
      return prev.slice(0, clamped);
    });
  };

  // Identify cases that would lose their existing screenshot if the
  // adviser saves the new (smaller) structure. Each entry is the
  // 0-based index plus the original label so the confirm dialog can
  // list them by name.
  const orphanedCases = [];
  for (let i = count; i < (file.caseCount || 0); i++) {
    if (screenshotsByIndex[i]) {
      orphanedCases.push({
        index: i,
        label: (file.caseLabels || [])[i] || `Case ${i + 1}`,
      });
    }
  }

  const handleSave = () => {
    setLocalError(null);
    const trimmed = labels.map((s) => (typeof s === 'string' ? s.trim() : ''));
    if (trimmed.some((s) => !s)) {
      setLocalError('Every case needs a description.');
      return;
    }
    if (orphanedCases.length > 0 && !confirmStep) {
      setConfirmStep(true);
      return;
    }
    onApply(count, trimmed);
  };

  if (confirmStep) {
    return (
      <div style={styles.modalOverlay} onClick={() => !actionLoading && setConfirmStep(false)}>
        <motion.div
          className="card"
          style={styles.modal}
          onClick={(e) => e.stopPropagation()}
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
        >
          <h3 style={{ marginBottom: '0.75rem', color: 'var(--cp-warning)' }}>
            Delete {orphanedCases.length} screenshot{orphanedCases.length === 1 ? '' : 's'}?
          </h3>
          <p style={{ color: 'var(--cp-text)', fontSize: '0.85rem', marginBottom: '0.6rem' }}>
            Reducing to {count} case{count === 1 ? '' : 's'} will delete the screenshot{orphanedCases.length === 1 ? '' : 's'} for:
          </p>
          <ul style={{ margin: '0 0 1rem 0', paddingLeft: '1.2rem', color: 'var(--cp-text)', fontSize: '0.85rem' }}>
            {orphanedCases.map((c) => (
              <li key={c.index} style={{ marginBottom: '0.25rem' }}>
                <strong>Case {c.index + 1}</strong> — “{c.label}”
              </li>
            ))}
          </ul>
          <p style={{ color: 'var(--cp-danger)', fontSize: '0.8rem', marginBottom: '1rem' }}>
            This cannot be undone. The screenshots will be permanently removed from storage.
          </p>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              className="btn btn-outline"
              onClick={() => setConfirmStep(false)}
              disabled={actionLoading}
            >
              Cancel
            </button>
            <button
              style={styles.confirmDeleteBtn}
              onClick={handleSave}
              disabled={actionLoading}
            >
              {actionLoading ? 'Saving…' : 'Continue'}
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div style={styles.modalOverlay} onClick={() => !actionLoading && onClose()}>
      <motion.div
        className="card"
        style={{ ...styles.modal, ...styles.modalWide }}
        onClick={(e) => e.stopPropagation()}
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <h3 style={{ marginBottom: '0.75rem' }}>Edit cases</h3>
        <p style={{ color: 'var(--cp-text-muted)', fontSize: '0.85rem', marginBottom: '1rem' }}>
          Adjust how many transactions this file contains and update the description for each case. Descriptions are read back verbatim onto the new business handover memo PDF, so write them comprehensively.
        </p>
        {localError && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: '0.4rem',
            padding: '0.55rem 0.75rem', background: 'rgba(192, 57, 43, 0.08)',
            border: '1px solid rgba(192, 57, 43, 0.2)', borderRadius: '8px',
            color: 'var(--cp-danger)', fontSize: '0.8rem', marginBottom: '0.75rem',
          }}>
            <AlertTriangle size={14} /> {localError}
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', marginBottom: '1rem' }}>
          <label style={{ fontSize: '0.85rem', fontWeight: 500, color: 'var(--cp-text)' }}>
            Number of cases
          </label>
          <input
            type="number"
            min={1}
            max={10}
            value={count}
            onChange={(e) => handleCountChange(e.target.value)}
            style={{ width: '5rem', textAlign: 'center', fontWeight: 600 }}
            disabled={actionLoading}
          />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '1rem' }}>
          {labels.map((label, idx) => (
            <div key={idx} style={styles.editCaseCard}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                <span style={{ ...styles.caseListTag, marginTop: '0.4rem' }}>Case {idx + 1}</span>
                <textarea
                  value={label}
                  onChange={(e) => {
                    const v = e.target.value;
                    setLabels((prev) => prev.map((l, i) => (i === idx ? v : l)));
                  }}
                  placeholder={'e.g.\nAllan Gray — Tax-Free Savings Account\nNew investment of R 36 000 (annual lump sum)'}
                  style={{
                    flex: 1,
                    minHeight: '5rem',
                    resize: 'vertical',
                    fontFamily: 'inherit',
                    fontSize: '0.9rem',
                    lineHeight: 1.45,
                    padding: '0.55rem 0.7rem',
                  }}
                  disabled={actionLoading}
                  rows={3}
                />
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-outline" onClick={onClose} disabled={actionLoading}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={actionLoading}>
            {actionLoading ? 'Saving…' : 'Save'}
          </button>
        </div>
      </motion.div>
    </div>
  );
}

// Modal style keys shared by the case-screenshot modals (F1a). Defined once
// here; OverrideUploadModal, FlagDetailModal and AggregateDiagnosticsModal
// import them, and FileDetail re-attaches the shared keys to its own styles
// object. The import direction is always view -> component, never back.
export const styles = {
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '1rem',
  },
  modal: {
    maxWidth: '420px',
    width: '100%',
    padding: '1.5rem',
  },
  modalWide: {
    maxWidth: '720px',
    maxHeight: '90vh',
    overflowY: 'auto',
  },
  editCaseCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    padding: '0.65rem 0.75rem',
    background: '#F7FAFC',
    border: '1px solid #E1E8F0',
    borderRadius: '10px',
  },
  confirmDeleteBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.4rem',
    padding: '0.65rem 1.25rem',
    background: 'var(--cp-danger)',
    color: 'var(--cp-surface)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  caseListTag: {
    flexShrink: 0,
    minWidth: '4.5rem',
    padding: '0.3rem 0.55rem',
    background: 'rgba(27, 154, 170, 0.12)',
    color: 'var(--cp-primary)',
    fontSize: '0.75rem',
    fontWeight: 600,
    borderRadius: '999px',
    textAlign: 'center',
  },
};
